import { forwardRef, useEffect, useId, useImperativeHandle, useState } from 'react';

import { autoUpdate, FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react';
import { AtSymbolIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import type { Editor } from '@tiptap/core';
import cn from 'classnames';

import { MENTION_BORDER_CLASS_NAME } from '../../../utils/mentions/mentionStyle';

import type { MentionSuggestion } from '../../CommunityPostComposer.type';

export interface MentionSuggestionListProps {
  editor: Editor;
  label: string;
  items: MentionSuggestion[];
  onSelect: (item: MentionSuggestion) => void;
  clientRect: (() => DOMRect | null) | null;
}

export interface MentionSuggestionListHandle {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export const MentionSuggestionList = forwardRef<MentionSuggestionListHandle, MentionSuggestionListProps>(
  ({ editor, label, items, onSelect, clientRect }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listId = useId();

    const { refs, floatingStyles } = useFloating({
      placement: 'bottom-start',
      middleware: [offset(8), flip(), shift({ padding: 8 })],
      whileElementsMounted: autoUpdate,
    });

    useEffect(() => {
      refs.setPositionReference({
        getBoundingClientRect: () => clientRect?.() ?? new DOMRect(),
      });
    }, [clientRect, refs]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset the selection when a new list of suggestions arrives
    useEffect(() => setSelectedIndex(0), [items]);

    // The editor keeps focus while the list is open, so it points screen readers at the list and the selected option.
    // TipTap gives the editor role="textbox", which doesn't allow aria-expanded.
    useEffect(() => {
      const editorElement = editor.view.dom;
      if (items.length > 0) {
        editorElement.setAttribute('aria-controls', listId);
        editorElement.setAttribute('aria-activedescendant', optionId(listId, selectedIndex));
      }
      return () => {
        editorElement.removeAttribute('aria-controls');
        editorElement.removeAttribute('aria-activedescendant');
      };
    }, [editor, items, listId, selectedIndex]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) onSelect(item);
    };

    const moveSelection = (step: number) => {
      const newIndex = (selectedIndex + items.length + step) % items.length;
      setSelectedIndex(newIndex);
      document.getElementById(optionId(listId, newIndex))?.scrollIntoView({ block: 'nearest' });
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (!items.length) return false;

        if (event.key === 'ArrowUp') {
          moveSelection(-1);
          return true;
        }

        if (event.key === 'ArrowDown') {
          moveSelection(1);
          return true;
        }

        if (event.key === 'Enter' || event.key === 'Tab') {
          selectItem(selectedIndex);
          return true;
        }

        return false;
      },
    }));

    if (!items.length) return null;

    return (
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="z-[100] min-w-48 max-w-72 rounded-lg border border-black/10 bg-white shadow-lg"
        >
          <div id={listId} role="listbox" aria-label={label} className="max-h-72 overflow-y-auto py-1">
            {items.map((item, index) => (
              // biome-ignore lint/a11y/useKeyWithClickEvents: the editor keeps focus and handles the keys, pointing at the option with aria-activedescendant
              <div
                key={item.id}
                id={optionId(listId, index)}
                role="option"
                aria-selected={index === selectedIndex}
                tabIndex={-1}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectItem(index)}
                className={cn('flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm', {
                  'bg-black/5': index === selectedIndex,
                  'hover:bg-black/5': index !== selectedIndex,
                })}
              >
                <SuggestionAvatar item={item} />
                <span className="min-w-0 flex-1 truncate text-black/90">{item.name}</span>
                {item.type !== 'member' && (
                  <>
                    {' '}
                    <span className="shrink-0 text-xs text-black/50">{item.member_count.toLocaleString()}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </FloatingPortal>
    );
  },
);

MentionSuggestionList.displayName = 'MentionSuggestionList';

const SuggestionAvatar = ({ item }: { item: MentionSuggestion }) => {
  if (item.type === 'member') {
    return item.avatar_url ? (
      <img src={item.avatar_url} alt="" className="size-7 shrink-0 rounded-full object-cover" />
    ) : (
      <span className="size-7 shrink-0 rounded-full bg-gray-200" />
    );
  }

  const Icon = item.type === 'everyone' ? AtSymbolIcon : CheckBadgeIcon;
  return (
    <span
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-full bg-[var(--community-mention-bg,rgb(0_0_0/0.1))] text-black/70',
        MENTION_BORDER_CLASS_NAME,
      )}
    >
      <Icon className="size-4" aria-hidden="true" />
    </span>
  );
};

const optionId = (listId: string, index: number) => `${listId}-option-${index}`;
